import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Grid,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Autocomplete,
} from "@mui/material";
import { Delete, Edit, Visibility } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { HiSparkles, HiEye, HiPencil, HiTrash } from "react-icons/hi2";
import api from "../../services/api";
import AppPagination from "../Tools/AppPagination";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteBike,
  getBikeNames,
  getNewBikes,
} from "../../State/NewBike/Action";
import { useMyContext } from "../../store/ContextApi";

export default function NewBikeList() {
  const [bikeId, setBikeId] = useState("");
  const [page, setPage] = useState(0);
  const [model, setModel] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { isAdmin } = useMyContext();

  const bikes = useSelector(
    (store) => store.newBikesResponse.newBikesPaged.bikes
  );
  const totalPages = useSelector(
    (store) => store.newBikesResponse.newBikesPaged.totalPages
  );
  const bikeNames = useSelector(
    (store) => store.newBikesResponse.bikeNames.bikeModelNames
  );

  const deleted = useSelector((store) => store.newBikesResponse.bikeDeleted);

  useEffect(() => {
    const modelId = new URLSearchParams(location.search).get("model");
    if (modelId) {
      setModel(modelId);
    }
    dispatch(getBikeNames());
    loadBikes();
  }, [page, model]);

  const loadBikes = () => {
    dispatch(getNewBikes(page, model));
  };

  const handleUpdate = (bikeId) =>
    navigate(`/update-new-bike?bikeId=${bikeId}`);
  const handleDelete = () => {
    dispatch(deleteBike(bikeId));
    if (deleted) {
      api.put(`/newBikes/${bikeId}/delete`).then(() => {
        setOpen(false);
        loadBikes();
        window.scrollTo(0, 0);
      });
    }
  };
  const handleViewMore = (bikeId) =>
    navigate(`/newBikeDetail?bikeId=${bikeId}`);
  const handleBikeChange = (modelObj) => {
    setModel(modelObj ? modelObj.id : ""); // Check for null before accessing 'id'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Modern Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center mb-4">
            <HiSparkles className="w-8 h-8 text-accent-500 mr-3" />
            <h1 className="text-4xl font-bold bg-gradient-to-r from-primary-600 to-accent-600 bg-clip-text text-transparent">
              New Bikes
            </h1>
          </div>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Discover the latest models with cutting-edge technology and premium features
          </p>
        </motion.div>

        {/* Modern Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="flex justify-center mb-8"
        >
          <Autocomplete
            onChange={(event, value) => handleBikeChange(value)}
            options={bikeNames}
            getOptionLabel={(option) => option.name}
            onInputChange={(event, value, reason) => {
              if (reason === "clear") {
                setModel(""); // Reset model when "X" is clicked
              }
            }}
            sx={{
              width: 400,
              backgroundColor: "white",
              borderRadius: "16px",
              boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
              "& .MuiOutlinedInput-root": {
                borderRadius: "16px",
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#3b82f6",
                },
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search New Bikes"
                placeholder="Find your perfect ride..."
                variant="outlined"
              />
            )}
          />
        </motion.div>

        {/* Modern Bike Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8"
        >
          {bikes?.map((bike, index) => (
            <motion.div
              key={bike.bikeId}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              className="group"
            >
              <Card 
                className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden border-0"
                sx={{ 
                  borderRadius: "16px",
                  "&:hover": {
                    boxShadow: "0 25px 50px rgba(0,0,0,0.15)"
                  }
                }}
              >
                <div className="relative overflow-hidden">
                  <CardMedia
                    component="img"
                    height="240"
                    image={bike.imageUrl}
                    alt={bike.brand + " " + bike.model}
                    onClick={() => handleViewMore(bike.bikeId)}
                    className="cursor-pointer transition-transform duration-300 group-hover:scale-110"
                    sx={{
                      width: "100%",
                      height: "240px",
                      objectFit: "contain",
                      backgroundColor: "#f5f5f5",
                      borderTopLeftRadius: "16px",
                      borderTopRightRadius: "16px",
                    }}
                  />
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-lg">
                    <HiSparkles className="w-5 h-5 text-accent-500" />
                  </div>
                </div>
                
                <CardContent className="p-6">
                  <Typography 
                    variant="h6" 
                    className="font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors"
                    sx={{ fontSize: "1.1rem" }}
                  >
                    {bike.brand} {bike.model}
                  </Typography>
                  
                  <Typography 
                    variant="body2" 
                    className="text-gray-500 mb-3"
                    sx={{ fontSize: "0.9rem" }}
                  >
                    {bike.engineCapacity} CC Engine
                  </Typography>
                  
                  <div className="flex items-center justify-between mb-4">
                    <Typography 
                      variant="h6" 
                      className="font-bold bg-gradient-to-r from-primary-600 to-accent-600 bg-clip-text text-transparent"
                      sx={{ fontSize: "1.2rem" }}
                    >
                      ₹{bike.exShowroomPrice.toLocaleString()}/-
                    </Typography>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      variant="contained"
                      onClick={() => handleViewMore(bike.bikeId)}
                      className="flex-1 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-xl py-2 font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
                      startIcon={<HiEye className="w-4 h-4" />}
                      sx={{
                        borderRadius: "12px",
                        textTransform: "none",
                        fontSize: "0.9rem",
                        fontWeight: 600,
                        py: 1
                      }}
                    >
                      View
                    </Button>
                    {isAdmin && (
                      <>
                        <Button
                          variant="contained"
                          onClick={() => handleUpdate(bike.bikeId)}
                          className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white rounded-xl py-2 font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
                          startIcon={<HiPencil className="w-4 h-4" />}
                          sx={{
                            borderRadius: "12px",
                            textTransform: "none",
                            fontSize: "0.9rem",
                            fontWeight: 600,
                            py: 1,
                            minWidth: "auto",
                            px: 2
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="contained"
                          onClick={() => {
                            setBikeId(bike.bikeId);
                            setOpen(true);
                          }}
                          className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl py-2 font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
                          startIcon={<HiTrash className="w-4 h-4" />}
                          sx={{
                            borderRadius: "12px",
                            textTransform: "none",
                            fontSize: "0.9rem",
                            fontWeight: 600,
                            py: 1,
                            minWidth: "auto",
                            px: 2
                          }}
                        >
                          Delete
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {/* Modern Pagination */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="flex justify-center mt-12"
        >
          <Box className="bg-white rounded-2xl shadow-lg p-4">
            <AppPagination setPage={setPage} totalPages={totalPages} />
          </Box>
        </motion.div>
      </div>

      {/* Modern Delete Dialog */}
      <Dialog 
        open={open} 
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 25px 50px rgba(0,0,0,0.15)"
          }
        }}
      >
        <DialogTitle 
          textAlign="center" 
          fontWeight="bold"
          sx={{ 
            fontSize: "1.5rem",
            color: "#dc2626",
            py: 3
          }}
        >
          Delete Bike?
        </DialogTitle>
        <DialogContent sx={{ px: 4, pb: 2 }}>
          <DialogContentText 
            textAlign="center"
            sx={{ 
              fontSize: "1.1rem",
              color: "#6b7280"
            }}
          >
            Are you sure you want to delete this bike? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: 2, p: 3 }}>
          <Button 
            onClick={() => setOpen(false)}
            variant="outlined"
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              px: 4,
              py: 1
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            variant="contained"
            startIcon={<HiTrash className="w-4 h-4" />}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              px: 4,
              py: 1,
              backgroundColor: "#dc2626",
              "&:hover": {
                backgroundColor: "#b91c1c"
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
